import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history } from '@umijs/max';
import { message, notification } from 'antd';
import axios from 'axios';
import { refreshToken } from '@/services/ant-design-pro/api';
import {
  completeInflightRequest,
  isAuthAllowlistRequest,
  isLogoutDraining,
  LogoutDrainingError,
  registerInflightRequest,
} from '@/utils/authSessionManager';
import { createLogger } from '@/utils/logger';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isTokenExpiringSoon,
  setTokens,
} from '@/utils/token';

const logger = createLogger('RequestErrorConfig');

// 用于防止刷新token时的并发请求
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
let refreshStartTime: number | null = null;
const REFRESH_TIMEOUT = 10000; // 10秒超时

// 创建一个全局的 message 和 notification 实例
// 在非组件环境中，我们使用静态 API，虽然会显示警告但不影响功能
// 为了减少警告，我们创建一个包装函数
// 使用 any 类型避免类型检查问题，因为这些实例在运行时是有效的
let globalMessage: any = null;
let globalNotification: any = null;

// 初始化全局实例（在 app.tsx 中调用）
export function initGlobalMessageInstances(
  messageInstance: any,
  notificationInstance: any,
) {
  globalMessage = messageInstance;
  globalNotification = notificationInstance;
}

// 获取 message 实例
const getMessageInstance = () => {
  // 如果全局实例存在，使用它；否则使用静态 API（会有警告但功能正常）
  return globalMessage || message;
};

// 获取 notification 实例
const getNotificationInstance = () => {
  return globalNotification || notification;
};

/**
 * 重置刷新状态
 */
export function resetRefreshState(): void {
  isRefreshing = false;
  refreshPromise = null;
  refreshStartTime = null;
}

/**
 * 检查并重置异常状态
 */
function checkAndResetState(): void {
  // 检查超时
  if (
    isRefreshing &&
    refreshStartTime &&
    Date.now() - refreshStartTime > REFRESH_TIMEOUT
  ) {
    console.warn('刷新token超时，重置状态');
    resetRefreshState();
    return;
  }

  // 如果状态异常（比如刷新标志为true但没有promise），重置状态
  if (isRefreshing && !refreshPromise) {
    console.warn('检测到异常状态，重置刷新状态');
    resetRefreshState();
  }
}

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: async (error: any, opts: any) => {
      const requestId = Number(
        error?.config?.__requestId ?? opts?.__requestId ?? 0,
      );
      completeInflightRequest(requestId);

      if (
        error?.name === 'LogoutDrainingError' ||
        error?.message === 'LOGOUT_DRAINING'
      ) {
        logger.info('登出排水中，拦截请求并短路', {
          url: error?.url ?? error?.config?.url ?? opts?.url,
        });
        throw error;
      }

      // 登出排水阶段触发的取消请求，静默忽略
      if (
        error?.name === 'AbortError' ||
        error?.message?.includes?.('logout-draining')
      ) {
        logger.info('登出排水中，静默取消在途请求', {
          url: error?.config?.url ?? opts?.url,
        });
        throw error;
      }

      if (opts?.skipErrorHandler) throw error;

      // 处理401未授权错误 - 尝试刷新token
      if (error.response?.status === 401) {
        if (isLogoutDraining()) {
          throw error;
        }

        const refresh = getRefreshToken();
        const requestUrl = (opts?.url ?? error.config?.url ?? '').toString();

        // 如果是登录接口返回401，不处理，直接抛出让登录页面处理
        if (requestUrl.includes('/v1/auth/login')) {
          throw error;
        }

        // 如果是刷新token接口本身返回401，不再尝试刷新，直接跳转登录
        if (requestUrl.includes('/v1/auth/refresh')) {
          console.error('刷新token失败，跳转登录页');
          clearTokens();
          getMessageInstance().error('登录已过期，请重新登录');
          // 延迟跳转，让用户看到提示
          setTimeout(() => {
            history.push('/user/login');
          }, 1000);
          throw error;
        }

        // 如果有refresh token，尝试刷新
        const access = getAccessToken();
        if (refresh && access) {
          if (isLogoutDraining()) {
            throw error;
          }
          try {
            // 如果正在刷新，等待刷新完成
            if (isRefreshing && refreshPromise) {
              await refreshPromise;
              // 刷新完成后，重新发起原请求
              const originalRequest = error.config;
              if (originalRequest) {
                const newToken = getAccessToken();
                if (newToken) {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  try {
                    // 使用 axios 重新发起请求（直接调用，绕过拦截器避免循环）
                    const retryResponse = await axios(originalRequest);
                    return retryResponse;
                  } catch (retryError: any) {
                    // 重试也失败，检查是否还是401
                    if (retryError.response?.status === 401) {
                      clearTokens();
                      getMessageInstance().error('登录已过期，请重新登录');
                      setTimeout(() => {
                        history.push('/user/login');
                      }, 1000);
                    }
                    throw retryError;
                  }
                }
              }
              throw error;
            }

            // 开始刷新
            isRefreshing = true;
            refreshStartTime = Date.now();

            refreshPromise = refreshToken(refresh, access);
            const result = await refreshPromise;

            if (result.data?.access_token && result.data?.refresh_token) {
              // Token Rotation: 更新新的 access_token 和 refresh_token
              // 注意：返回的 refresh_token 是新的，旧的 refresh_token 已失效
              setTokens(
                result.data.access_token,
                result.data.refresh_token, // 新的 refresh_token，旧的已失效
                result.data.expires_in,
              );
              console.log(
                'Token刷新成功（被动刷新），已更新新的 refresh_token',
              );
              isRefreshing = false;
              refreshPromise = null;

              // 刷新成功后，重新发起原请求
              const originalRequest = error.config;
              if (originalRequest) {
                const newToken = getAccessToken();
                if (newToken) {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${newToken}`;
                  try {
                    // 使用 axios 重新发起请求（直接调用，绕过拦截器避免循环）
                    const retryResponse = await axios(originalRequest);
                    getMessageInstance().success('会话已自动更新');
                    return retryResponse;
                  } catch (retryError: any) {
                    console.error('重试请求失败:', retryError);
                    // 重试也失败，检查是否还是401
                    if (retryError.response?.status === 401) {
                      clearTokens();
                      getMessageInstance().error('登录已过期，请重新登录');
                      setTimeout(() => {
                        history.push('/user/login');
                      }, 1000);
                    }
                    throw retryError;
                  }
                }
              }
              // 如果刷新成功但无法重试，抛出原错误
              throw error;
            } else {
              // 刷新返回的数据不完整
              console.error('刷新token返回数据不完整');
              isRefreshing = false;
              refreshPromise = null;
              clearTokens();
              getMessageInstance().error('会话数据异常，请重新登录');
              setTimeout(() => {
                history.push('/user/login');
              }, 1000);
              throw error;
            }
          } catch (refreshError) {
            console.error('刷新token失败:', refreshError);
            isRefreshing = false;
            refreshPromise = null;
            clearTokens();
            getMessageInstance().error('登录已过期，请重新登录');
            setTimeout(() => {
              history.push('/user/login');
            }, 1000);
            throw error;
          }
        } else {
          // 没有refresh token，直接跳转登录
          console.error('无refresh token，跳转登录页');
          clearTokens();
          getMessageInstance().error('请先登录');
          setTimeout(() => {
            history.push('/user/login');
          }, 1000);
          throw error;
        }
      }

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              getMessageInstance().warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              getMessageInstance().error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              getNotificationInstance().open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              getMessageInstance().error(errorMessage);
          }
        }
        throw error;
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        // 401、404、409、422 都不显示全局错误消息：
        // - 401 已在上面处理
        // - 404 是正常情况，由具体业务逻辑处理
        // - 409 CONFLICT 由具体业务逻辑处理（如邀请冲突）
        // - 422 验证错误由具体业务逻辑处理，显示后端返回的具体 message
        if (
          error.response.status !== 401 &&
          error.response.status !== 404 &&
          error.response.status !== 409 &&
          error.response.status !== 422
        ) {
          const responseData = error.response?.data;
          const errorMsg =
            responseData?.message ||
            responseData?.detail ||
            `Response status:${error.response.status}`;
          getMessageInstance().error(errorMsg);
        }
        throw error;
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        getMessageInstance().error('None response! Please retry.');
        throw error;
      } else {
        // 发送请求时出了点问题
        getMessageInstance().error('Request error, please retry.');
        throw error;
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (config: RequestOptions) => {
      const requestUrl = (config.url ?? '').toString();

      if (isLogoutDraining() && !isAuthAllowlistRequest(requestUrl)) {
        logger.info('请求拦截：登出排水中禁止业务请求', { url: requestUrl });
        throw new LogoutDrainingError(requestUrl);
      }

      const inflight = registerInflightRequest(requestUrl);
      if (inflight) {
        (config as any).__requestId = inflight.requestId;
        (config as any).signal = inflight.signal;
      }

      // 检查并重置异常状态
      checkAndResetState();

      // 获取访问令牌
      const token = getAccessToken();

      // 如果token存在，添加到请求头
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };

        // 检查token是否即将过期（3分钟内），如果是则异步刷新
        if (isTokenExpiringSoon() && !isRefreshing && !isLogoutDraining()) {
          const refresh = getRefreshToken();
          const access = getAccessToken();
          if (refresh && access) {
            // 异步刷新，不阻塞当前请求
            isRefreshing = true;
            refreshStartTime = Date.now();

            refreshPromise = refreshToken(refresh, access)
              .then((result) => {
                if (result.data?.access_token && result.data?.refresh_token) {
                  // Token Rotation: 更新新的 access_token 和 refresh_token
                  // 注意：返回的 refresh_token 是新的，旧的 refresh_token 已失效
                  setTokens(
                    result.data.access_token,
                    result.data.refresh_token, // 新的 refresh_token，旧的已失效
                    result.data.expires_in,
                  );
                  console.log('Token异步刷新成功，已更新新的 refresh_token');
                  resetRefreshState();
                } else {
                  console.warn('异步刷新返回数据不完整，等待被动刷新处理');
                  resetRefreshState();
                }
              })
              .catch((error) => {
                console.error('异步刷新token失败:', error);
                resetRefreshState();
                // 异步刷新失败不影响当前请求，等待401时被动刷新处理
                // 不清除token，由401处理逻辑统一管理
              });
          }
        }
      }

      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      const requestId = Number((response?.config as any)?.__requestId ?? 0);
      completeInflightRequest(requestId);

      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as ResponseStructure;

      if (data?.success === false) {
        getMessageInstance().error('请求失败！');
      }
      return response;
    },
  ],
};
